import { Box, Button, Divider, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import type { BoxMenu, BoxMenuItem } from '@/types';
import { formatPrice } from '@/utils/price.utils';

interface BoxWizardMenuProps {
  menu: BoxMenu;
  currentKey: string;
  onSelect: (item: BoxMenuItem) => void;
  onGoToResume: () => void;
}

/** Raggruppa gli item per `groupLabel` mantenendo l'ordine di comparsa. */
function groupItems(items: BoxMenuItem[]): { label: string; items: BoxMenuItem[] }[] {
  const groups: { label: string; items: BoxMenuItem[] }[] = [];
  for (const item of items) {
    let group = groups.find((g) => g.label === item.groupLabel);
    if (!group) {
      group = { label: item.groupLabel, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

/** Menu laterale del wizard Box: gruppi (UNIONE / per-box) con selezioni + totale + CTA riepilogo. */
export function BoxWizardMenu({ menu, currentKey, onSelect, onGoToResume }: BoxWizardMenuProps) {
  const groups = groupItems(menu.items);
  const answered = menu.items.filter((i) => i.answered).length;
  const allAnswered = menu.items.length > 0 && answered === menu.items.length;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        position: { md: 'sticky' },
        top: { md: 88 },
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
        Avanzamento — {answered}/{menu.items.length} scelte
      </Typography>

      {groups.map((group) => (
        <Box key={group.label} sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}
          >
            {group.label}
          </Typography>
          {group.items.map((item) => {
            const active = item.key === currentKey;
            return (
              <Box
                key={item.key}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(item);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  mt: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {item.answered ? (
                  <CheckCircleIcon fontSize="small" color="success" aria-hidden="true" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" aria-hidden="true" />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: active ? 600 : 400 }}>
                    {item.label}
                  </Typography>
                  {item.selection && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {item.selection}
                    </Typography>
                  )}
                </Box>
                {item.priceDiff > 0 && (
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    + {formatPrice(item.priceDiff)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          Totale
        </Typography>
        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 750 }}>
          {formatPrice(menu.total)}
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={!allAnswered}
        onClick={onGoToResume}
        sx={{ mt: 2 }}
      >
        Vai al riepilogo
      </Button>
    </Box>
  );
}
